import { useCallback } from 'react';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { moveToSearchResultPage } from '../logics/moveToSearchResultPage';

/**
 * 検索処理を行うカスタムフック
 *
 * @param {SelectedCurrentConditionType} selectedSearchCondition - 選択された検索条件
 * @param {string | undefined} fidQuery - フィーチャーIDクエリ
 * @returns {Object} - 検索ボタンのクリックハンドラ
 */
export const useSearch = (
  selectedSearchCondition: SelectedCurrentConditionType,
  fidQuery: string | undefined
) => {
  /**
   * サイドバー上での検索処理
   */
  const onClickSearchButton = useCallback(() => {
    moveToSearchResultPage({ selectedSearchCondition, fidQuery });
  }, [fidQuery, selectedSearchCondition]);

  /**
   * 職種モーダルでの検索処理
   *
   * @param {Object} selectedOccupation - 選択された職種情報
   * @param {number} selectedOccupation.searchJobTypeLId - 大分類職種ID
   * @param {number} selectedOccupation.searchJobTypeMId - 中分類職種ID
   * @param {number} selectedOccupation.searchJobTypeSId - 小分類職種ID
   */
  const onClickOccupationModalSearchButton = useCallback(
    (selectedOccupation: {
      searchJobTypeLId: SelectedCurrentConditionType['searchJobTypeLId'];
      searchJobTypeMId: SelectedCurrentConditionType['searchJobTypeMId'];
      searchJobTypeSId: SelectedCurrentConditionType['searchJobTypeSId'];
    }) => {
      moveToSearchResultPage({
        selectedSearchCondition: {
          ...selectedSearchCondition,
          ...selectedOccupation,
        },
        fidQuery,
      });
    },
    [fidQuery, selectedSearchCondition]
  );

  /**
   * 勤務地モーダルでの検索処理
   *
   * @param {Object} selectedLocation - 選択された勤務地情報
   * @param {number} selectedLocation.areaId - 地域ID
   * @param {number} selectedLocation.prefectureId - 都道府県ID
   * @param {number} selectedLocation.cityId - 市区町村ID
   * @param {number} selectedLocation.wardId - 区ID
   */
  const onClickLocationModalSearchButton = useCallback(
    (selectedLocation: {
      areaId: SelectedCurrentConditionType['areaId'];
      prefectureId: SelectedCurrentConditionType['prefectureId'];
      cityId: SelectedCurrentConditionType['cityId'];
      wardId: SelectedCurrentConditionType['wardId'];
    }) => {
      moveToSearchResultPage({
        selectedSearchCondition: {
          ...selectedSearchCondition,
          ...selectedLocation,
        },
        fidQuery,
      });
    },
    [fidQuery, selectedSearchCondition]
  );

  /**
   * 路線駅モーダルでの検索処理
   *
   * @param {Object} selectedRouteStation - 選択された路線駅情報
   * @param {number} selectedRouteStation.routeId - 路線ID
   * @param {number} selectedRouteStation.stationId - 駅ID
   */
  const onClickRoteStationModalSearchButton = useCallback(
    (selectedRouteStation: {
      routeId: SelectedCurrentConditionType['routeId'];
      stationId: SelectedCurrentConditionType['stationId'];
    }) => {
      moveToSearchResultPage({
        selectedSearchCondition: {
          ...selectedSearchCondition,
          ...selectedRouteStation,
        },
        fidQuery,
      });
    },
    [fidQuery, selectedSearchCondition]
  );

  /**
   * 業種モーダルでの検索処理
   *
   * @param {Object} selectedIndustry - 選択された業種情報
   * @param {number[]} selectedIndustry.industryLId - 大分類業種ID
   * @param {number[]} selectedIndustry.industrySId - 小分類業種ID
   */
  const onClickIndustryModalSearchButton = useCallback(
    (selectedIndustry: {
      industryLId: SelectedCurrentConditionType['industryLId'];
      industrySId: SelectedCurrentConditionType['industrySId'];
    }) => {
      moveToSearchResultPage({
        selectedSearchCondition: {
          ...selectedSearchCondition,
          ...selectedIndustry,
        },
        fidQuery,
      });
    },
    [fidQuery, selectedSearchCondition]
  );

  return {
    onClickSearchButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRoteStationModalSearchButton,
    onClickIndustryModalSearchButton,
  };
};
